// Artisanal code & CSS built by hand by @ladi. Because every single model hilariously failed trying to make this...
import { IconCheck } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';
import { useMemo } from 'react';
import { match } from 'ts-pattern';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { Text } from '~/domains/design-system/Text';

import type { LoyaltyTier } from '../../../../types/gamification.types';
import { TIER_COLORS } from '../../constants';
import type { TierStatus } from '../../types/whalersClub.types';
import { getTierRewardPct } from '../../utils/getTierRewardPct';

import { useStyles } from './WhalersClubProgressBar.styles';
import getTierIcon from './TierIcons';

interface WhalersClubProgressBarProps {
  tiers: LoyaltyTier[];
  tierStatus: TierStatus;
}

function WhalersClubProgressBar({
  tiers,
  tierStatus,
}: WhalersClubProgressBarProps) {
  const { classes, cx } = useStyles({ columns: tiers.length });
  const { t } = useTranslation('loyalty');

  const {
    rewardBasis,
    currentTierName,
    currentTierIndex,
    currentProgressAmount,
    currentRewardAmount,
    currentTierRewardPct,
    currentProgressPct,
    isLastTier,
  } = tierStatus;

  // Display styles controls
  const hasProgress = currentProgressAmount > 0;
  const achievedFirstTier = currentTierIndex >= 0;

  // Progress bar has "tier" markers in the middle of the columns
  // We need to recalculate the base percentage & shift the progress bar by 1/2 column, to align the progress bar with the tier markers
  const tierBasePct = 1 / tiers.length * 100;
  const currentBasePct = achievedFirstTier
  ? (tierBasePct / 2) + (tierBasePct * currentTierIndex)
  : 0;
  // Calculate the relative progress percentage to the next tier,
  // But we need to calculate the relative progress to the 3rd marker (because marker shift) if there is no tier
  const relativeProgressPct = achievedFirstTier
    ? (currentProgressPct / 100) * tierBasePct
    : currentProgressPct * (tierBasePct / 100 * 2.5);

  // Shift the progress bar by 10px to align with the tier markers, defaults to 16px for empty bar
  // eslint-disable-next-line no-nested-ternary
  const progressWidth = hasProgress
    ? isLastTier
      ? '100%'
      : `calc(${currentBasePct + relativeProgressPct}% + 10px)`
    : '16px';

  // Dynamic tooltip position based on the progress bar position
  // eslint-disable-next-line no-nested-ternary
  const tooltipPosition = currentTierIndex >= 0
    ? isLastTier ? 'right' : 'center'
    : relativeProgressPct > 12 ? 'center' : 'left';
  const isDark = isLastTier;

  // Specific color override for the initial tier once the progress is started
  const { background, progressGradient } = !currentTierName && hasProgress
    ? TIER_COLORS.Bronze
    : TIER_COLORS[currentTierName] || TIER_COLORS.Default;

  const basisCopy = match(rewardBasis)
    .with('handle', () => ({
      tierRewardKey: 'whalersClub.progressBar.percentBack' as const,
      rewardTooltip: t('whalersClub.progressBar.amountBack', { amount: CurrencyConvertor(currentRewardAmount) }),
      callToActionLine2: t('whalersClub.progressBar.callToActionLine2'),
    }))
    .with('net_loss', () => ({
      tierRewardKey: 'whalersClub.progressBar.percent' as const,
      rewardTooltip: t('whalersClub.progressBar.percentOnLosses', { percent: currentTierRewardPct }),
      callToActionLine2: t('whalersClub.progressBar.callToActionLine2LossBack'),
    }))
    .exhaustive();

  const tooltipText = currentTierName
    ? basisCopy.rewardTooltip
    : t('whalersClub.progressBar.amountPlayed', { amount: CurrencyConvertor(currentProgressAmount) });

  const { tierRewardKey } = basisCopy;
  const tierColumns = useMemo(() =>
    tiers.map((tier) => (
      <div key={tier.id} className={classes.marker}>
        <Text variant="subhead-small" className={isDark && classes.darkMarkerText}>{tier.name}</Text>
        <Text variant="subhead-x-small" className={isDark && classes.darkMarkerText}>{CurrencyConvertor(Math.round(tier.minValue))}+</Text>
        <Text variant="subhead-x-small" className={classes.percentBack}>
          {t(tierRewardKey, { percent: getTierRewardPct(tier) })}
        </Text>
      </div>
    ))
    , [tiers, isDark, tierRewardKey]);

  // Shift first two icons if no progress
  const tierIcons = useMemo(() =>
    tiers.map((tier, index) => !achievedFirstTier
      ? (
        <div key={tier.id} className={classes.trackMarker}>
          {(index > 1) && getTierIcon(index - 2)}
        </div>
      ) : (
        <div key={tier.id} className={classes.trackMarker}>
          {index <= currentTierIndex
            ? <IconCheck size={12} className={classes.checkIcon} stroke={3} />
            : getTierIcon(index)
          }
        </div>
      )
    ), [tiers, currentTierIndex, achievedFirstTier]);

  return (
    <div className={classes.container}>
      <div className={classes.rewardTooltipTrack} style={{ width: progressWidth }}>
        <div className={classes.rewardTooltipPosition}>
          <div className={cx(classes.rewardTooltip, [tooltipPosition])} style={{ backgroundColor: background }}>
            <Text variant="subhead-x-small">
              {tooltipText}
            </Text>
          </div>
        </div>
      </div>
      <div className={classes.track}>
        <div
          className={classes.fill}
          style={{
            width: progressWidth,
            background: progressGradient,
          }}
        />
        <div className={classes.markersContainer}>
          {tierIcons}
        </div>
      </div>
      <div className={classes.markersContainer}>
        {achievedFirstTier
          ? tierColumns
          : (<>
            <div className={cx(classes.marker, ['ctaMarker'])}>
              <Text variant="subhead-small">
                {t('whalersClub.progressBar.callToActionLine1')}
              </Text>
              <Text variant="subhead-small">{basisCopy.callToActionLine2}</Text>
            </div>
            {tierColumns.slice(0, 2)}
          </>
          )
        }
      </div>
    </div>
  )
}

export default WhalersClubProgressBar;
